import React from 'react';
import styles from './AboutCompanyOverview.module.scss';
import { BOX_TITLES, FUNDING_LABEL, INVESTOR_LABELS, SECTION_TITLES } from './AboutCompanyOverviewConstants';

interface AboutCompanyOverviewProps {
    overviewDataV2: any;
    overview?: string;
    sectorInfo?: any;
    invalidSectors?: any;
}

const AboutCompanyOverview: React.FC<AboutCompanyOverviewProps> = ({ overviewDataV2, overview, sectorInfo, invalidSectors }) => {
    // Check if any data is available across all potential data sources
    const hasOverviewContent = !!overviewDataV2?.overview?.overviewContent || !!overview;
    const hasSectorInfo = !!(
        overviewDataV2?.sectorDetails?.sector ||
        overviewDataV2?.sectorDetails?.subSector ||
        sectorInfo?.sector ||
        sectorInfo?.subSector
    );
    const hasKeyInvestors = !!(overviewDataV2?.overview?.keyInvestors && overviewDataV2?.overview?.keyInvestors.length > 0);
    const hasFundingRaised = !!overviewDataV2?.overview?.fundingRaised;

    // Business aspect data structure for mapping with title mapping
    const businessAspects = [
        { key: 'productOfferings', titleKey: 'PRODUCT_OFFERINGS', content: overviewDataV2?.overview?.productOfferings },
        {
            key: 'customersAndSalesChannels',
            titleKey: 'CUSTOMERS_AND_SALES_CHANNELS',
            content: overviewDataV2?.overview?.customersAndSalesChannels,
        },
        {
            key: 'vendorsSourcingManufacturing',
            titleKey: 'VENDORS_SOURCING_MANUFACTURING',
            content: overviewDataV2?.overview?.vendorsSourcingManufacturing,
        },
        {
            key: 'geographiesSegmentation',
            titleKey: 'GEOGRAPHIES_SEGMENTATION',
            content: overviewDataV2?.overview?.geographiesSegmentation,
        },
    ];

    // Additional boxes data structure for mapping with title mapping
    const additionalBoxes = [
        { key: 'sectoralPerformance', titleKey: 'SECTORAL_PERFORMANCE', content: overviewDataV2?.sectorDetails?.sectoralPerformance },
        {
            key: 'competitorsOrListedPeer',
            titleKey: 'COMPETITORS_OR_LISTED_PEER',
            content: overviewDataV2?.overview?.competitorsOrListedPeer,
        },
    ];

    const hasBusinessAspects = businessAspects.some((aspect) => !!aspect.content);
    const hasAdditionalBoxes = additionalBoxes.some((box) => !!box.content);

    // If no data is available across any field, don't render anything
    if (!hasOverviewContent && !hasSectorInfo && !hasKeyInvestors && !hasFundingRaised && !hasBusinessAspects && !hasAdditionalBoxes) {
        return null;
    }

    const overviewData = overviewDataV2?.overview;

    return (
        <div className={styles.AboutCompanyOverview}>
            {/* Overview Content Section */}
            {hasOverviewContent && (
                <>
                    <div className={styles.Title}>{SECTION_TITLES.OVERVIEW}</div>
                    <div className={styles.Text}>{overviewData?.overviewContent || overview}</div>
                </>
            )}

            {/* Funding Raised and Key Investors Pills */}
            {(hasFundingRaised || hasKeyInvestors) && (
                <div className={styles.Badges}>
                    {overviewData?.fundingRaised && (
                        <div className={styles.Pill}>
                            <span className={styles.PillLabel}>{FUNDING_LABEL}</span> <span>{overviewData?.fundingRaised}</span>
                        </div>
                    )}

                    {hasKeyInvestors && (
                        <>
                            <p className={styles.Separator}>{' | '}</p>
                            {overviewData?.keyInvestors.length === 1 && (
                                <span className={styles.InvestorLabel}>{INVESTOR_LABELS.SINGLE}</span>
                            )}
                            {overviewData?.keyInvestors.length > 1 && (
                                <span className={styles.InvestorLabel}>{INVESTOR_LABELS.MULTIPLE}</span>
                            )}
                            {overviewData?.keyInvestors.map((investor, index) => (
                                <div key={`investor-${index}`} className={`${styles.Pill} ${styles.PrimaryPill}`}>
                                    <span>{investor}</span>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}

            {/* Business Aspect Boxes */}
            {hasBusinessAspects && (
                <div className={styles.BusinessAspectGrid}>
                    {businessAspects.map(
                        (aspect) =>
                            aspect.content && (
                                <div key={aspect.key} className={styles.BusinessAspectBox}>
                                    <div className={styles.BoxTitle}>{BOX_TITLES[aspect.titleKey]}</div>
                                    <div className={styles.BoxContent}>{aspect.content}</div>
                                </div>
                            ),
                    )}
                </div>
            )}

            {/* Sector Information */}
            {hasSectorInfo && (
                <>
                    <div className={`${styles.Title} ${hasOverviewContent ? styles.SectorTitle : ''}`}>{SECTION_TITLES.COMPANY_SECTOR}</div>
                    <div className={styles.Badges}>
                        {!invalidSectors?.includes((overviewDataV2?.sectorDetails?.sector || sectorInfo?.sector) ?? '') && (
                            <div className={styles.Pill}>{overviewDataV2?.sectorDetails?.sector || sectorInfo?.sector}</div>
                        )}
                        {!invalidSectors?.includes((overviewDataV2?.sectorDetails?.subSector || sectorInfo?.subSector) ?? '') && (
                            <div className={styles.Pill}>{overviewDataV2?.sectorDetails?.subSector || sectorInfo?.subSector}</div>
                        )}
                    </div>
                </>
            )}

            {/* Additional Boxes - Competitors and Sectoral Performance */}
            {hasAdditionalBoxes && (
                <div className={styles.AdditionalBoxesGrid}>
                    {additionalBoxes.map(
                        (box) =>
                            box.content && (
                                <div key={box.key} className={styles.AdditionalDetailsBox}>
                                    <div className={styles.BoxTitle}>{BOX_TITLES[box.titleKey]}</div>
                                    <div className={styles.BoxContent}>{box.content}</div>
                                </div>
                            ),
                    )}
                </div>
            )}
        </div>
    );
};

export default AboutCompanyOverview;
